import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { appointmentsAPI, availabilityAPI, blockedDatesAPI } from '../services/api';
import { formatDate, formatTime } from '../utils/helpers';
import { format } from 'date-fns';

const { FiLogOut, FiCalendar, FiClock, FiUsers, FiSettings, FiTrash2, FiEdit, FiPlus, FiX, FiRefreshCw, FiFilter } = FiIcons;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('appointments');
  const [appointments, setAppointments] = useState([]);
  const [availabilitySettings, setAvailabilitySettings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all');
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
    } else {
      loadData();
    }
  }, [isAuthenticated, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('Loading admin dashboard data...');
      
      const [appointmentsRes, availabilityRes, blockedRes] = await Promise.all([
        appointmentsAPI.getAll({ includeTotal: true, limit: 100 }),
        availabilityAPI.getAll(),
        blockedDatesAPI.getAll()
      ]);
      
      console.log('Admin data loaded:', {
        appointments: appointmentsRes.data,
        availability: availabilityRes.data,
        blocked: blockedRes.data
      });
      
      setAppointments(appointmentsRes.data || []);
      setAvailabilitySettings(availabilityRes.data || []);
      setBlockedDates(blockedRes.data || []);
      
      // Removed success toast for cleaner production experience
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load dashboard data. Please refresh to try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
    toast.success('Logged out successfully');
  };

  const handleDeleteAppointment = async (id) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await appointmentsAPI.delete(id);
        toast.success('Appointment deleted successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to delete appointment');
      }
    }
  };

  const handleUpdateAppointmentStatus = async (id, status) => {
    try {
      await appointmentsAPI.update(id, {
        status,
        updated_at: new Date().toISOString()
      });
      toast.success('Appointment status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update appointment status');
    }
  };

  const handleAddBlockedDate = async (date, reason) => {
    try {
      await blockedDatesAPI.create({
        blocked_date: date,
        reason: reason || 'Not available',
        created_at: new Date().toISOString()
      });
      toast.success('Date blocked successfully');
      loadData();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to block date');
    }
  };

  const handleRemoveBlockedDate = async (id) => {
    if (window.confirm('Are you sure you want to unblock this date?')) {
      try {
        await blockedDatesAPI.delete(id);
        toast.success('Date unblocked successfully');
        loadData();
      } catch (error) {
        toast.error('Failed to unblock date');
      }
    }
  };

  const getDayName = (dayNumber) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNumber] || 'Unknown';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'status-badge confirmed';
      case 'pending':
        return 'status-badge pending';
      case 'cancelled':
        return 'status-badge cancelled';
      default:
        return 'status-badge';
    }
  };

  const filteredAppointments = appointments.filter(apt => {
    if (filter === 'all') return true;
    if (filter === 'today') {
      return apt.appointment_date === format(new Date(), 'yyyy-MM-dd');
    }
    if (filter === 'upcoming') {
      return apt.appointment_date > format(new Date(), 'yyyy-MM-dd');
    }
    return apt.status === filter;
  });

  if (loading && appointments.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading dashboard...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiSettings} className="text-xl text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-gray-600">Appointment Management System</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={loadData} 
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" 
                title="Refresh data"
              >
                <SafeIcon icon={FiRefreshCw} className="text-xl" />
              </button>
              <button 
                onClick={handleLogout} 
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="card card-hover"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiUsers} className="text-2xl text-blue-600" />
                </div>
                <span className="text-sm text-gray-500">Total</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{appointments.length}</h3>
              <p className="text-gray-600 text-sm mt-1">Total Appointments</p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }} 
            className="card card-hover"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiCalendar} className="text-2xl text-green-600" />
                </div>
                <span className="text-sm text-gray-500">Today</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {appointments.filter(apt => 
                  apt.appointment_date === format(new Date(), 'yyyy-MM-dd') && 
                  apt.status === 'confirmed'
                ).length}
              </h3>
              <p className="text-gray-600 text-sm mt-1">Confirmed Today</p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.2 }} 
            className="card card-hover"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiX} className="text-2xl text-red-600" />
                </div>
                <span className="text-sm text-gray-500">Blocked</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{blockedDates.length}</h3>
              <p className="text-gray-600 text-sm mt-1">Blocked Dates</p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.3 }} 
            className="card card-hover"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiClock} className="text-2xl text-purple-600" />
                </div>
                <span className="text-sm text-gray-500">Schedule</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{availabilitySettings.length}</h3>
              <p className="text-gray-600 text-sm mt-1">Active Days</p>
            </div>
          </motion.div>
        </div>

        {/* Enhanced Tabs */}
        <div className="card mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-1 p-2">
              {[
                { id: 'appointments', label: 'Appointments', icon: FiCalendar, count: appointments.length },
                { id: 'availability', label: 'Availability', icon: FiClock, count: availabilitySettings.length },
                { id: 'blocked', label: 'Blocked Dates', icon: FiX, count: blockedDates.length }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 border border-blue-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon icon={tab.icon} className="mr-2" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">All Appointments</h2>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiFilter} className="text-gray-500" />
                    <select 
                      value={filter} 
                      onChange={(e) => setFilter(e.target.value)} 
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="all">All Appointments</option>
                      <option value="today">Today</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="pending">Pending</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                {filteredAppointments.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Visitor
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date & Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredAppointments.map((appointment) => (
                          <tr key={appointment.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {appointment.visitor_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {appointment.visitor_email}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {appointment.visitor_phone}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {formatDate(appointment.appointment_date)}
                              </div>
                              <div className="text-sm text-gray-500">
                                {formatTime(appointment.appointment_time)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {appointment.appointment_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={getStatusColor(appointment.status)}>
                                {appointment.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <select 
                                  value={appointment.status} 
                                  onChange={(e) => handleUpdateAppointmentStatus(appointment.id, e.target.value)}
                                  className="text-xs border border-gray-300 rounded-lg px-2 py-1"
                                >
                                  <option value="confirmed">Confirmed</option>
                                  <option value="pending">Pending</option>
                                  <option value="cancelled">Cancelled</option>
                                </select>
                                <button 
                                  onClick={() => handleDeleteAppointment(appointment.id)} 
                                  className="text-red-600 hover:text-red-900 p-1"
                                >
                                  <SafeIcon icon={FiTrash2} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <SafeIcon icon={FiCalendar} className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No appointments found</p>
                  </div>
                )}
              </div>
            )}

            {/* Availability Tab */}
            {activeTab === 'availability' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Availability Settings</h2>
                </div>
                <div className="grid gap-4">
                  {availabilitySettings.length > 0 ? (
                    availabilitySettings.map((setting) => (
                      <div key={setting.id} className="card p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {getDayName(setting.day_of_week)}
                            </h3>
                            <p className="text-gray-600 mt-1">
                              {setting.is_available 
                                ? `Available: ${formatTime(setting.start_time)} - ${formatTime(setting.end_time)}`
                                : 'Not Available'
                              }
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              setting.is_available 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {setting.is_available ? 'Available' : 'Unavailable'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <SafeIcon icon={FiClock} className="text-6xl text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No availability settings configured</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Blocked Dates Tab */}
            {activeTab === 'blocked' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Blocked Dates</h2>
                  <button 
                    onClick={() => {
                      setModalType('blockDate');
                      setShowModal(true);
                    }} 
                    className="btn-primary flex items-center"
                  >
                    <SafeIcon icon={FiPlus} className="mr-2" />
                    Block Date
                  </button>
                </div>
                <div className="grid gap-4">
                  {blockedDates.length > 0 ? (
                    blockedDates.map((blocked) => (
                      <div key={blocked.id} className="card p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {formatDate(blocked.blocked_date)}
                            </h3>
                            <p className="text-gray-600 mt-1">{blocked.reason}</p>
                          </div>
                          <button 
                            onClick={() => handleRemoveBlockedDate(blocked.id)} 
                            className="text-red-600 hover:text-red-900 p-2"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <SafeIcon icon={FiX} className="text-6xl text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No blocked dates</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Block Date Modal */}
      {showModal && modalType === 'blockDate' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }} 
            className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">Block Date</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleAddBlockedDate(formData.get('date'), formData.get('reason'));
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input 
                  type="date" 
                  name="date" 
                  required 
                  min={format(new Date(), 'yyyy-MM-dd')} 
                  className="form-input" 
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason
                </label>
                <input 
                  type="text" 
                  name="reason" 
                  placeholder="Reason for blocking (optional)" 
                  className="form-input" 
                />
              </div>
              <div className="flex space-x-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Block Date
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;